import { useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { getListProjectExperience } from "actions/RFQ/Supplier/rfqActions";
import {
	SmartFieldQuestionnaireBase,
	filterSmartFieldOptionForSubmittedRFQ,
} from "./SmartFieldQuestionnaireBase";
import { useSmartFieldSelection } from "../hooks/useSmartFieldSelection";
import {
	ProjectExperienceCard,
	ProjectExperienceCardSkeleton,
} from "pages/RFQ/RFQ/CreateRFQ/PreviewRFQ/Questionnaire/_components/ProjectExperience";
import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";
import { useCurrentCompanyId } from "hooks/auth";

const projectDummyData = {
	id: 0,
	name: "Example Project Card",
	value: 100,
	year_completed: new Date(Date.now()).getFullYear(),
	client_name: "Project Client",
	procurementMethodIds: [1],
};

export const SmartFieldProjectExperience = ({
	className,
	question,
	required,
	isClientPreview,
	answer,
	...props
}) => {
	const [search, setSearch] = useState("");
	const [selectedFilter, setSelectedFilter] = useState([]);
	const companyId = useCurrentCompanyId();
	const {
		rfxId,
		isLoading: isSubmissionLoading,
		isClientView,
	} = useRfxSubmissionData();
	const { value, error, touched, isSelected, selectHandler, isSubmitting } =
		useSmartFieldSelection(props, { shouldSubmit: true });

	const queryParams = {
		search: search,
		filters: selectedFilter,
		replacement: 1,
		rfq_id: rfxId,
	};

	const useAnswerData =
		(props?.disabled && answer?.selectedProjects?.length > 0) ||
		!rfxId ||
		isClientView;

	const {
		data,
		isLoading,
		error: queryError,
	} = useQuery({
		queryKey: ["projectExperience", queryParams],
		queryFn: getListProjectExperience,

		enabled: !isSubmissionLoading && !useAnswerData && !isClientPreview,
	});

	// if the project_experience is loaded already, use that (as we may have just transitioned from edit to view)
	const projectExperience =
		data?.project_experience ?? answer?.selectedProjects ?? [];
	const filteringList = data?.filters || [];

	// filter data for preview mode
	const filteredData = filterSmartFieldOptionForSubmittedRFQ(
		projectExperience,
		value,
		props?.disabled
	);

	const selectedIds = Array.isArray(value) ? value : value ? [value] : [];
	const selectedData = projectExperience.filter((project) =>
		selectedIds.includes(project.id)
	);

	if (isClientPreview) {
		filteredData.push(projectDummyData);
	}

	return (
		<SmartFieldQuestionnaireBase
			className={className}
			question={question}
			required={required}
			description="Please choose the projects you would like to showcase."
			searchPlaceholder="Search project or client"
			isLoading={isLoading}
			filteringList={filteringList}
			selectedFilter={selectedFilter}
			setSelectedFilter={setSelectedFilter}
			setSearch={setSearch}
			error={error}
			touched={touched}
			type="project-experience"
			selectedData={selectedData}
			showFilter={!props?.disabled}
			showSearch={!props?.disabled}
			isClientPreview={isClientPreview}
			{...props}
		>
			{isLoading ? (
				[...Array(3)].map((_, index) => (
					<ProjectExperienceCardSkeleton key={index} />
				))
			) : queryError ? (
				<div className="text-red-500">
					Error loading projects: {queryError.message || "Unknown error"}
				</div>
			) : filteredData.length === 0 ? (
				<div className="italic text-gray-500">
					No projects {useAnswerData ? "selected" : "found"}
				</div>
			) : (
				filteredData.map((project) => (
					<ProjectExperienceCard
						key={project.id}
						companyId={companyId}
						values={project}
						rfqSubmission={true}
						selected={isSelected(project?.id)}
						selectHandler={() => selectHandler(project?.id)}
						isClientPreview={isClientPreview}
						isPreview={props?.disabled}
						supplierSmartField
						isSubmitting={isSubmitting}
					/>
				))
			)}
		</SmartFieldQuestionnaireBase>
	);
};
